import {
  trackCalculatorComplete,
  trackCtaClick,
} from "@/components/analytics/conversions";
import { BodyFatFaq } from "@/components/calculators/body-fat-faq";
import { BodyFatGuide } from "@/components/calculators/body-fat-guide";
import { MeasurementField } from "@/components/calculators/measurement-field";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { storeFfmiHandoff } from "@/utils/ffmi-handoff";
import { seo } from "@/utils/seo";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_app/essay/body-fat-calculator")({
  component: BodyFatCalculator,
  head: () => ({
    meta: seo({
      title: "Yağ Oranı Hesaplama ve Ölçüm Rehberi | AtalayTheCoach",
      description:
        "Navy yöntemiyle boy, boyun, bel ve kalça ölçülerinden vücut yağ oranını tahmin et. Ölçüm rehberi, örnek hesaplama, Türkiye araştırmaları ve sık sorulan sorular.",
      keywords:
        "vücut yağ oranı, body fat hesaplama, mezura ölçümü, navy method, vücut kompozisyonu, Alican Atalay",
      canonical: "https://atalaythecoach.com/essay/body-fat-calculator",
    }),
    links: [
      {
        rel: "canonical",
        href: "https://atalaythecoach.com/essay/body-fat-calculator",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline:
            "Vücut Yağ Oranı Hesaplama: Yöntemler, Ölçüm ve Sonuçları Anlama",
          author: {
            "@type": "Organization",
            name: "AtalayTheCoach",
            url: "https://atalaythecoach.com",
          },
          dateModified: "2026-09-28",
          inLanguage: "tr",
          mainEntityOfPage:
            "https://atalaythecoach.com/essay/body-fat-calculator",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Ana sayfa",
              item: "https://atalaythecoach.com",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Yağ oranı hesaplama",
              item: "https://atalaythecoach.com/essay/body-fat-calculator",
            },
          ],
        }),
      },
    ],
  }),
});

// Navy Method (Çevre Ölçümleri)
function calculateNavyMethod(
  gender: string,
  height: number,
  waist: number,
  neck: number,
  hip?: number,
): number {
  if (gender === "male") {
    return (
      495 /
        (1.0324 -
          0.19077 * Math.log10(waist - neck) +
          0.15456 * Math.log10(height)) -
      450
    );
  } else {
    if (!hip) return 0;
    return (
      495 /
        (1.29579 -
          0.35004 * Math.log10(waist + hip - neck) +
          0.221 * Math.log10(height)) -
      450
    );
  }
}

function BodyFatCalculator() {
  const [gender, setGender] = useState("");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [waist, setWaist] = useState("");
  const [neck, setNeck] = useState("");
  const [hip, setHip] = useState("");
  const [error, setError] = useState("");
  const [results, setResults] = useState<{
    bodyFat: number;
    fatMass?: number;
    leanMass?: number;
  } | null>(null);

  const calculateBodyFat = () => {
    setResults(null);
    setError("");
    const measurements = [
      height,
      waist,
      neck,
      ...(gender === "female" ? [hip] : []),
      ...(weight ? [weight] : []),
    ];
    if (
      !gender ||
      measurements.some(
        (value) => !Number.isFinite(Number(value)) || Number(value) <= 0,
      )
    ) {
      setError(
        "Cinsiyet seçin ve ölçülerinizi sıfırdan büyük sayılarla doldurun.",
      );
      return;
    }
    const bodyFat = calculateNavyMethod(
      gender,
      Number(height),
      Number(waist),
      Number(neck),
      gender === "female" ? Number(hip) : undefined,
    );
    if (!Number.isFinite(bodyFat) || bodyFat <= 0 || bodyFat >= 100) {
      setError(
        "Bu ölçülerle geçerli bir tahmin üretilemedi. Ölçüm noktalarını ve santimetre değerlerini kontrol edin.",
      );
      return;
    }
    const fatMass = weight ? (Number(weight) * bodyFat) / 100 : undefined;
    setResults({
      bodyFat,
      fatMass,
      leanMass: fatMass === undefined ? undefined : Number(weight) - fatMass,
    });
    trackCalculatorComplete("body_fat");
  };

  return (
    <main className="min-h-screen bg-background pt-32 pb-16 text-foreground">
      <div className="container mx-auto max-w-6xl px-4">
        <nav
          aria-label="İçerik yolu"
          className="mb-8 text-muted-foreground text-sm"
        >
          <a href="/" className="hover:underline">
            Ana sayfa
          </a>{" "}
          / <span aria-current="page">Yağ oranı hesaplama</span>
        </nav>
        <header className="mb-12 text-center">
          <h1 className="mb-6 font-bold text-4xl leading-tight md:text-5xl lg:text-6xl">
            Vücut Yağ Oranı Hesaplama
          </h1>
          <p className="mx-auto max-w-3xl text-lg text-muted-foreground leading-relaxed md:text-xl">
            Navy yöntemiyle vücut yağ oranını tahmin et. Boyunu ve mezurayla
            aldığın çevre ölçülerini santimetre olarak gir.
          </p>
        </header>
        <section
          id="hesaplayici"
          aria-label="Yağ oranı hesaplayıcı"
          className="mx-auto w-full max-w-[40rem] scroll-mt-28 pb-12"
        >
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              calculateBodyFat();
            }}
            onChangeCapture={() => {
              setResults(null);
              setError("");
            }}
            className="space-y-6"
          >
            <fieldset>
              <legend className="mb-3 font-medium text-sm">Cinsiyet</legend>
              <RadioGroup
                aria-label="Cinsiyet"
                value={gender}
                onValueChange={(value) => {
                  setGender(value);
                  setResults(null);
                  setError("");
                }}
                className="flex gap-6"
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="male-bf" value="male" />
                  <Label htmlFor="male-bf">Erkek</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="female-bf" value="female" />
                  <Label htmlFor="female-bf">Kadın</Label>
                </div>
              </RadioGroup>
            </fieldset>
            <div className="grid gap-6 sm:grid-cols-2">
              <MeasurementField
                id="height"
                label="Boy (cm)"
                unit="cm"
                value={height}
                onChange={setHeight}
                placeholder="175"
              />
              <MeasurementField
                id="neck"
                label="Boyun Çevresi"
                unit="cm"
                value={neck}
                onChange={setNeck}
                placeholder="38"
                hint="Gırtlak çıkıntısının hemen altından ölç."
              />
              <MeasurementField
                id="waist"
                label={
                  gender === "male"
                    ? "Karın (göbek deliği hizası)"
                    : gender === "female"
                      ? "Bel (en dar nokta)"
                      : "Bel / karın çevresi"
                }
                unit="cm"
                value={waist}
                onChange={setWaist}
                placeholder="85"
                hint={
                  gender === "male"
                    ? "Karnını içeri çekmeden ölç."
                    : gender === "female"
                      ? "Doğal belinin en dar çevresini ölç."
                      : "Ölçüm noktasını görmek için cinsiyet seç."
                }
              />
              {gender === "female" && (
                <MeasurementField
                  id="hip"
                  label="Kalça Çevresi"
                  unit="cm"
                  value={hip}
                  onChange={setHip}
                  placeholder="100"
                  hint="Kalçanın en geniş çevresini ölç."
                />
              )}
              <MeasurementField
                id="weight"
                label="Kilo (kg) — isteğe bağlı"
                unit="kg"
                value={weight}
                onChange={setWeight}
                placeholder="70"
                hint="Yağ ve yağsız kütleni kilogram olarak görmek için."
              />
            </div>
            {error && (
              <p role="alert" className="text-destructive text-sm">
                {error}
              </p>
            )}
            <Button type="submit" className="h-11 w-full">
              Vücut Yağ Oranını Hesapla <ArrowRight className="ml-2 size-4" />
            </Button>
            <p className="text-muted-foreground text-sm leading-relaxed">
              Yetişkinler için yaklaşık tahmin. Sonuç bir tanı veya kişisel
              sağlık hedefi değildir.
            </p>
          </form>
          <div aria-live="polite" aria-atomic="true">
            {results && (
              <section
                aria-label="Hesaplama sonucu"
                className="mt-10 grid gap-6 py-6 md:grid-cols-[1fr_2fr] md:gap-12"
              >
                <div>
                  <h2 className="mb-3 font-bold text-2xl">
                    Tahmini yağ oranın
                  </h2>
                  <p className="font-bold text-4xl text-primary">
                    {results.bodyFat.toFixed(1)}%
                  </p>
                  <p className="mt-2 text-muted-foreground text-sm">
                    Navy yöntemi
                  </p>
                </div>
                <div className="space-y-5">
                  {results.fatMass !== undefined &&
                    results.leanMass !== undefined && (
                      <dl className="grid grid-cols-2 gap-6">
                        <div>
                          <dt className="text-muted-foreground text-sm">
                            Yağ kütlesi
                          </dt>
                          <dd className="mt-1 font-semibold text-xl">
                            {results.fatMass.toFixed(1)} kg
                          </dd>
                        </div>
                        <div>
                          <dt className="text-muted-foreground text-sm">
                            Yağsız kütle
                          </dt>
                          <dd className="mt-1 font-semibold text-xl">
                            {results.leanMass.toFixed(1)} kg
                          </dd>
                        </div>
                      </dl>
                    )}
                  <p className="text-muted-foreground leading-relaxed">
                    Yağsız kütle yalnızca kas değildir. Değişimini aynı yöntem
                    ve ölçüm koşullarıyla takip et.
                  </p>
                  <a
                    href="#sonucu-yorumlama"
                    className="inline-block text-primary underline underline-offset-4"
                  >
                    Sonucumu nasıl yorumlarım?
                  </a>
                  <aside className="rounded-xl border border-primary/20 bg-primary/5 p-5">
                    <h3 className="font-semibold text-lg">
                      Sonucunu bir adım ileri taşı
                    </h3>
                    <p className="mt-2 text-muted-foreground text-sm leading-relaxed">
                      Boy, kilo ve tahmini yağ oranını kullanarak FFMI değerini
                      hesapla.
                    </p>
                    <Button
                      asChild
                      className="mt-4 h-auto min-h-11 w-full whitespace-normal py-2.5"
                    >
                      <a
                        href="/essay/ffmi-calculator"
                        onClick={() => {
                          storeFfmiHandoff({
                            gender: gender as "male" | "female",
                            height: Number(height),
                            bodyFat: results.bodyFat,
                            ...(weight ? { weight: Number(weight) } : {}),
                          });
                          trackCtaClick(
                            "body_fat_to_ffmi",
                            "/essay/ffmi-calculator",
                          );
                        }}
                      >
                        FFMI Değerini Hesapla
                        <ArrowRight className="ml-2 size-4 shrink-0" />
                      </a>
                    </Button>
                  </aside>
                </div>
              </section>
            )}
          </div>
        </section>
        <BodyFatGuide />
        <BodyFatFaq />
      </div>
    </main>
  );
}
